import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  FileCheck, FileText, Download, Check, Loader2, X, Eye, Lock, ShieldCheck
} from "lucide-react";
import SignatureCanvas from "./SignatureCanvas";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Policy signing step for OnboardingWizard.
 * Employees preview each of the 6 Big Kreation policies, draw their signature,
 * type their full name, tick "I agree", click Sign — backend merges a
 * signature cert into the PDF and stores it in their HR folder.
 *
 * Props:
 *   prefillName: optional full name to prefill the "Typed name" field
 *   authToken:  JWT used as X-Admin-Auth fallback when the HttpOnly cookie is
 *               blocked (Safari/iOS private, cross-site contexts, etc.)
 *   onAllSigned(): fired when completion hits 100% so wizard can advance
 */
const PolicySignStep = ({ prefillName = "", authToken = "", onAllSigned }) => {
  const [data, setData] = useState({ policies: [], signed_count: 0, total: 0, completion_pct: 0, all_signed: false });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [activePolicy, setActivePolicy] = useState(null);
  const [downloadingKey, setDownloadingKey] = useState("");

  const authHeaders = useCallback(() => (
    authToken ? { "X-Admin-Auth": authToken } : {}
  ), [authToken]);

  const fetchPolicies = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const r = await axios.get(`${API_URL}/api/onboarding/policies`, {
        withCredentials: true,
        headers: authHeaders(),
      });
      setData(r.data);
      if (r.data?.all_signed && onAllSigned) onAllSigned();
    } catch (e) {
      console.error('[PolicySignStep] fetch failed:', e?.response?.status, e?.message);
      setLoadError(
        e?.response?.status === 401
          ? "Your session expired. Please log out and log in again."
          : "Couldn't load company policies. Please refresh the page — if this keeps happening, contact HR."
      );
    }
    setLoading(false);
  }, [authHeaders, onAllSigned]);

  useEffect(() => { fetchPolicies(); }, [fetchPolicies]);

  // Origin-independent signed-PDF download — fetches as an authenticated blob
  // (same bulletproof pattern as the preview iframe) and triggers a browser
  // download. Never depends on cross-origin <a target=_blank> + cookies.
  const downloadSignedPdf = useCallback(async (policyKey) => {
    setDownloadingKey(policyKey);
    try {
      const res = await axios.get(
        `${API_URL}/api/onboarding/policies/${policyKey}/signed-pdf`,
        {
          withCredentials: true,
          responseType: "blob",
          headers: authHeaders(),
        }
      );
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `signed_${policyKey}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      console.error('[PolicySignStep] signed-pdf download failed:', e?.response?.status, e?.message);
      alert("Couldn't download the signed PDF. Please refresh and try again.");
    }
    setDownloadingKey("");
  }, [authHeaders]);

  return (
    <div className="space-y-4" data-testid="policy-sign-step">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-bold text-[#1E1B4B] flex items-center gap-2">
            <ShieldCheck size={20} className="text-[#E83E8C]" />
            Big Kreation Company Policies
          </h3>
          <p className="text-xs text-gray-500">
            Parent company of DoozyGo® · every employee must read and sign all 6 policies
          </p>
        </div>
        <div className="text-right">
          <p className="text-sm font-bold text-[#1E1B4B]">{data.signed_count}/{data.total}</p>
          <div className="w-24 h-1.5 bg-gray-200 rounded-full overflow-hidden mt-1">
            <div
              className="h-full bg-gradient-to-r from-[#E83E8C] to-[#F59E0B] transition-all"
              style={{ width: `${data.completion_pct}%` }}
              data-testid="policy-progress-bar"
            />
          </div>
        </div>
      </div>

      {loadError && (
        <div
          className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-center justify-between gap-3"
          data-testid="policy-load-error"
        >
          <p className="text-xs text-red-700 font-medium">{loadError}</p>
          <button
            onClick={fetchPolicies}
            className="px-3 py-1.5 rounded-lg bg-red-500 text-white text-xs font-bold hover:bg-red-600"
            data-testid="policy-load-retry"
          >
            Retry
          </button>
        </div>
      )}

      {loading ? (
        <div className="py-8 flex justify-center"><Loader2 size={20} className="animate-spin text-[#E83E8C]" /></div>
      ) : (
        <div className="space-y-2">
          {data.policies.map((p) => (
            <div
              key={p.key}
              className={`border rounded-xl p-3 flex items-center gap-3 transition ${
                p.signed ? "border-green-300 bg-green-50" : "border-gray-200 bg-white hover:border-[#E83E8C]/30"
              }`}
              data-testid={`policy-row-${p.key}`}
            >
              <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${p.signed ? "bg-green-500" : "bg-[#FFF0F6]"}`}>
                {p.signed ? <Check size={18} className="text-white" /> : <FileText size={18} className="text-[#E83E8C]" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-semibold text-[#1E1B4B]">{p.title}</p>
                  <span className="text-[9px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 font-mono">{p.key}</span>
                  {p.signed && (
                    <span className="text-[9px] px-1.5 py-0.5 rounded bg-green-500 text-white font-semibold">Signed</span>
                  )}
                </div>
                <p className="text-[10px] text-gray-400 mt-0.5">
                  {p.signed
                    ? `Signed on ${new Date(p.signed_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}`
                    : "Read the full document, then sign to proceed"}
                </p>
              </div>
              {p.signed ? (
                <button
                  onClick={() => downloadSignedPdf(p.key)}
                  disabled={downloadingKey === p.key}
                  className="px-3 py-1.5 rounded-lg bg-white border border-gray-200 text-xs font-semibold text-gray-600 hover:border-[#E83E8C] flex items-center gap-1 disabled:opacity-60"
                  data-testid={`download-signed-${p.key}`}
                >
                  {downloadingKey === p.key ? (
                    <Loader2 size={12} className="animate-spin" />
                  ) : (
                    <Download size={12} />
                  )}
                  PDF
                </button>
              ) : (
                <button
                  onClick={() => setActivePolicy(p)}
                  className="px-3 py-1.5 rounded-lg bg-[#E83E8C] text-white text-xs font-bold hover:bg-[#c22d78] flex items-center gap-1"
                  data-testid={`sign-btn-${p.key}`}
                >
                  <FileCheck size={12} /> Read & Sign
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {data.all_signed && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-3 flex items-center gap-3" data-testid="all-signed-banner">
          <Check size={18} className="text-green-600" />
          <p className="text-sm text-green-700 font-semibold">All 6 company policies signed. You can submit onboarding now.</p>
        </div>
      )}

      {activePolicy && (
        <SignPolicyModal
          policy={activePolicy}
          prefillName={prefillName}
          authToken={authToken}
          onClose={() => setActivePolicy(null)}
          onSigned={() => { setActivePolicy(null); fetchPolicies(); }}
        />
      )}
    </div>
  );
};

// ════════════════════════════════════════════════════════════
// Modal: preview policy PDF + sign
// ════════════════════════════════════════════════════════════
const SignPolicyModal = ({ policy, prefillName, authToken = "", onClose, onSigned }) => {
  const [stage, setStage] = useState("read"); // read | sign
  const [signatureDataUrl, setSignatureDataUrl] = useState("");
  const [typedName, setTypedName] = useState(prefillName || "");
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // PERMANENT FIX: fetch the policy PDF as an authenticated blob and render it
  // through a same-origin `blob:` URL. This removes every cross-origin /
  // stale-domain / cookie-blocked failure mode that was breaking iframe
  // previews (e.g. a production bundle pointing at an old preview host).
  const [pdfBlobUrl, setPdfBlobUrl] = useState("");
  const [pdfLoadError, setPdfLoadError] = useState("");
  const [pdfLoading, setPdfLoading] = useState(true);

  useEffect(() => {
    let revoked = false;
    let currentUrl = "";
    (async () => {
      setPdfLoading(true);
      setPdfLoadError("");
      try {
        const res = await axios.get(
          `${API_URL}/api/onboarding/policies/${policy.key}/view`,
          {
            withCredentials: true,
            responseType: "blob",
            headers: authToken ? { "X-Admin-Auth": authToken } : {},
          }
        );
        if (revoked) return;
        const blob = new Blob([res.data], { type: "application/pdf" });
        currentUrl = URL.createObjectURL(blob);
        setPdfBlobUrl(currentUrl);
      } catch (e) {
        const status = e?.response?.status;
        setPdfLoadError(
          status === 401
            ? "Session expired — please log out and log in again."
            : "Couldn't load the policy PDF. You can still sign below — the official signed copy will be stored in your HR folder."
        );
        console.error('[SignPolicyModal] pdf fetch failed:', status, e?.message);
      } finally {
        if (!revoked) setPdfLoading(false);
      }
    })();
    return () => {
      revoked = true;
      if (currentUrl) URL.revokeObjectURL(currentUrl);
    };
  }, [policy.key, authToken]);

  const canSign = agreed && typedName.trim().length >= 2 && signatureDataUrl.length > 200;

  const submit = async () => {
    setSubmitting(true);
    setError("");
    try {
      await axios.post(
        `${API_URL}/api/onboarding/policies/${policy.key}/sign`,
        { signature_image: signatureDataUrl, typed_name: typedName.trim(), agreed },
        {
          withCredentials: true,
          headers: {
            "Content-Type": "application/json",
            ...(authToken ? { "X-Admin-Auth": authToken } : {}),
          },
        }
      );
      onSigned();
    } catch (e) {
      const status = e?.response?.status;
      setError(
        e?.response?.data?.detail ||
        (status === 401 ? "Session expired — please log out and log in again." :
         status ? `Failed to sign (HTTP ${status}). Please try again.` :
         "Network error while signing. Please check your connection and try again.")
      );
      console.error('[SignPolicyModal] sign failed:', status, e?.message);
    }
    setSubmitting(false);
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4"
      data-testid="sign-policy-modal"
    >
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[94vh] overflow-hidden flex flex-col shadow-2xl">
        {/* Header */}
        <div className="px-5 py-3 bg-gradient-to-r from-[#1E1B4B] to-[#E83E8C] text-white flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <Lock size={16} />
            <div className="min-w-0">
              <h3 className="font-bold text-sm truncate">{policy.title}</h3>
              <p className="text-[10px] text-white/70">Big Kreation Entertainment Pvt Ltd</p>
            </div>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white" data-testid="close-sign-modal">
            <X size={20} />
          </button>
        </div>

        {/* Step indicator */}
        <div className="px-5 py-2 border-b flex items-center gap-3 text-xs">
          <StepDot label="Read the policy" active={stage === "read"} done={stage === "sign"} />
          <div className="flex-1 h-px bg-gray-200" />
          <StepDot label="Sign & accept" active={stage === "sign"} done={false} />
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {stage === "read" ? (
            <div className="p-0 flex flex-col h-full">
              {pdfLoading ? (
                <div className="flex items-center justify-center" style={{ height: "55vh" }} data-testid="pdf-loading">
                  <Loader2 size={24} className="animate-spin text-[#E83E8C]" />
                </div>
              ) : pdfBlobUrl ? (
                <iframe
                  src={`${pdfBlobUrl}#view=FitH`}
                  title={policy.title}
                  className="w-full"
                  style={{ height: "55vh", border: "none" }}
                  data-testid="policy-pdf-iframe"
                />
              ) : (
                <div
                  className="flex flex-col items-center justify-center gap-3 p-6 text-center"
                  style={{ height: "55vh" }}
                  data-testid="pdf-error"
                >
                  <FileText size={36} className="text-gray-400" />
                  <p className="text-sm text-gray-600 max-w-md">{pdfLoadError}</p>
                </div>
              )}
              <div className="p-4 flex flex-wrap items-center justify-between gap-3 bg-gray-50 border-t">
                <p className="text-[11px] text-gray-500 flex items-center gap-1.5">
                  <Eye size={12} /> Please read the full policy before signing.
                </p>
                <div className="flex items-center gap-2">
                  {pdfBlobUrl && (
                    <a
                      href={pdfBlobUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="px-3 py-2 rounded-xl bg-white border border-gray-200 text-xs font-semibold text-gray-600 hover:border-[#E83E8C] flex items-center gap-1.5"
                      data-testid="open-policy-in-new-tab"
                    >
                      <Eye size={12} /> Open in new tab
                    </a>
                  )}
                  <button
                    onClick={() => setStage("sign")}
                    disabled={pdfLoading}
                    className="px-4 py-2 rounded-xl bg-[#E83E8C] text-white text-sm font-bold hover:bg-[#c22d78] disabled:opacity-40"
                    data-testid="proceed-to-sign"
                  >
                    I've read it → Sign
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="p-5 space-y-4">
              <div>
                <label className="text-xs font-semibold text-[#1E1B4B]">Full legal name (typed)</label>
                <input
                  value={typedName}
                  onChange={(e) => setTypedName(e.target.value)}
                  placeholder="e.g. Priya Sharma"
                  className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:border-[#E83E8C] outline-none"
                  data-testid="typed-name-input"
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-[#1E1B4B]">Draw your signature</label>
                <div className="mt-1">
                  <SignatureCanvas onChange={setSignatureDataUrl} height={160} />
                </div>
                <p className="text-[10px] text-gray-400 mt-1">
                  Use your finger on mobile, mouse on desktop, or stylus if you have one.
                </p>
              </div>

              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  className="accent-[#E83E8C] mt-0.5"
                  data-testid="policy-agree-checkbox"
                />
                <span className="text-xs text-gray-600 leading-relaxed">
                  I, <b>{typedName || "______"}</b>, confirm that I have read, understood and agree to be bound
                  by the <b>{policy.title}</b> of Big Kreation Entertainment Pvt Ltd. My electronic signature
                  above constitutes a binding acceptance.
                </span>
              </label>

              {error && <p className="text-xs text-red-500" data-testid="sign-error">{error}</p>}

              <div className="flex items-center justify-between pt-2">
                <button
                  onClick={() => setStage("read")}
                  className="text-xs text-gray-500 hover:text-[#E83E8C]"
                  data-testid="back-to-read"
                >
                  ← Back to policy
                </button>
                <button
                  onClick={submit}
                  disabled={!canSign || submitting}
                  className="px-5 py-2 rounded-xl bg-[#E83E8C] text-white text-sm font-bold hover:bg-[#c22d78] disabled:opacity-40 flex items-center gap-1.5"
                  data-testid="submit-signature-btn"
                >
                  {submitting ? <Loader2 size={14} className="animate-spin" /> : <FileCheck size={14} />}
                  {submitting ? "Signing…" : "Submit Signed Acceptance"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const StepDot = ({ label, active, done }) => (
  <div className="flex items-center gap-1.5">
    <span
      className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
        done ? "bg-green-500 text-white" : active ? "bg-[#E83E8C] text-white" : "bg-gray-200 text-gray-500"
      }`}
    >
      {done ? "✓" : ""}
    </span>
    <span className={`${active ? "text-[#E83E8C] font-semibold" : "text-gray-400"}`}>{label}</span>
  </div>
);

export default PolicySignStep;
